import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import {
  Bell,
  Check,
  Trash2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Info,
  Clock,
  DollarSign,
  FileText,
  MessageSquare,
  Loader2,
  Package,
  Box,
  Truck,
  User,
  Receipt
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import PushActivationPrompt from "./PushActivationPrompt";

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [stockBajo, setStockBajo] = useState({ critical: [], warning: [] });
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();

  const [mensajeriaAlertas, setMensajeriaAlertas] = useState([]);
  const [pagosAlertas, setPagosAlertas] = useState([]);
  const seenWaNotifIdsRef = useRef(null);

  const playWaAsignacionSound = () => {
    try {
      const audio = new Audio("/sounds/wa-asignacion.wav");
      audio.volume = 0.7;
      audio.play().catch(() => {});
    } catch (e) { /* noop */ }
  };

  // Check if user has specific permissions
  const hasPermission = (permissionKey) => {
    if (!user) return false;
    const role = user.role?.toLowerCase();
    if (role === 'admin' || role === 'administrador') return true;
    const perms = user.permissions || {};
    return perms[permissionKey] === true;
  };

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      
      // Always fetch user-specific notifications
      const promises = [
        axios.get(`${API}/notifications`, getAuthHeaders()).catch(() => ({ data: [] }))
      ];
      
      // Only fetch module-specific alerts if user has permission
      if (hasPermission('nomina') || hasPermission('gestion_humana')) {
        promises.push(
          axios.get(`${API}/nomina/alertas/historial?limit=5&estado=nueva`, getAuthHeaders()).catch(() => ({ data: { historial: [] } }))
        );
      } else {
        promises.push(Promise.resolve({ data: { historial: [] } }));
      }
      
      if (hasPermission('inventario')) {
        promises.push(
          axios.get(`${API}/inventario/alertas/stock-bajo`, getAuthHeaders()).catch(() => ({ data: { critical: [], warning: [], total: 0 } }))
        );
      } else {
        promises.push(Promise.resolve({ data: { critical: [], warning: [], total: 0 } }));
      }
      
      if (hasPermission('mensajeria')) {
        promises.push(
          axios.get(`${API}/mensajeria/alertas/pendientes`, getAuthHeaders()).catch(() => ({ data: { alertas: [], total: 0 } }))
        );
      } else {
        promises.push(Promise.resolve({ data: { alertas: [], total: 0 } }));
      }
      
      if (hasPermission('cuentas_pagar')) {
        promises.push(
          axios.get(`${API}/cuentas-pagar/alertas/pendientes`, getAuthHeaders()).catch(() => ({ data: { alertas: [], total: 0 } }))
        );
      } else {
        promises.push(Promise.resolve({ data: { alertas: [], total: 0 } }));
      }
      
      const [notifRes, alertasRes, stockRes, mensajeriaRes, pagosRes] = await Promise.all(promises);
      
      setNotifications(notifRes.data || []);
      const waNotifs = (notifRes.data || []).filter(n => n.related_entity_type === "whatsapp_conversacion" && !n.read);
      if (seenWaNotifIdsRef.current === null) {
        seenWaNotifIdsRef.current = new Set(waNotifs.map(n => n.id));
      } else {
        const nuevas = waNotifs.filter(n => !seenWaNotifIdsRef.current.has(n.id));
        if (nuevas.length > 0) playWaAsignacionSound();
        nuevas.forEach(n => seenWaNotifIdsRef.current.add(n.id));
      }
      setAlertas(alertasRes.data.historial || []);
      setStockBajo(stockRes.data || { critical: [], warning: [] });
      setMensajeriaAlertas(mensajeriaRes.data?.alertas || []);
      setPagosAlertas(pagosRes.data?.alertas || []);
      
      const unreadNotifs = (notifRes.data || []).filter(n => !n.read).length;
      const unreadAlertas = (alertasRes.data.historial || []).length;
      const stockAlerts = (stockRes.data?.critical?.length || 0) + (stockRes.data?.warning?.length || 0);
      const mensajeriaAlerts = mensajeriaRes.data?.total || 0;
      const pagosAlerts = pagosRes.data?.total || 0;
      setUnreadCount(unreadNotifs + unreadAlertas + (stockAlerts > 0 ? 1 : 0) + (mensajeriaAlerts > 0 ? 1 : 0) + (pagosAlerts > 0 ? 1 : 0));
    } catch (err) {
      console.error("Error fetching notifications:", err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, user]);

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    const onSwMessage = (e) => {
      if (e?.data?.type === "wa_asignacion") playWaAsignacionSound();
    };
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener("message", onSwMessage);
    }
    return () => {
      clearInterval(interval);
      if (navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener("message", onSwMessage);
      }
    };
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, getAuthHeaders());
      fetchNotifications();
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  const markAlertaAsRead = async (alertaId) => {
    try {
      await axios.put(`${API}/nomina/alertas/historial/${alertaId}/ver`, {}, getAuthHeaders());
      fetchNotifications();
    } catch (err) {
      console.error("Error marking alerta as read:", err);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, getAuthHeaders());
      fetchNotifications();
    } catch (err) {
      console.error("Error deleting notification:", err);
    }
  };

  const getIconByType = (type) => {
    switch (type) {
      case "success":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "error":
        return <XCircle className="w-5 h-5 text-red-500" />;
      case "warning":
        return <AlertCircle className="w-5 h-5 text-amber-500" />;
      case "prestamo":
      case "prestamo_por_finalizar":
      case "prestamo_atrasado":
        return <DollarSign className="w-5 h-5 text-amber-500" />;
      case "nomina_sin_procesar":
        return <FileText className="w-5 h-5 text-red-500" />;
      case "mensaje":
        return <MessageSquare className="w-5 h-5 text-blue-500" />;
      case "stock_critical":
        return <Box className="w-5 h-5 text-red-500" />;
      case "stock_warning":
        return <Package className="w-5 h-5 text-amber-500" />;
      default:
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "alta": return "bg-red-100 text-red-600";
      case "media": return "bg-amber-100 text-amber-600";
      default: return "bg-blue-100 text-blue-600";
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Ahora";
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours} h`;
    if (diffDays < 7) return `Hace ${diffDays} días`;
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
  };

  // Build stock alerts
  const stockAlerts = [];
  if (stockBajo.critical?.length > 0) {
    stockAlerts.push({
      id: "stock-critical",
      type: "stock_critical",
      title: `⚠️ ${stockBajo.critical.length} artículo(s) sin stock`,
      message: stockBajo.critical.slice(0, 3).map(a => a.nombre).join(", ") + (stockBajo.critical.length > 3 ? "..." : ""),
      priority: "alta",
      created_at: new Date().toISOString(),
      read: false,
      isStock: true,
      link: "/inventario/articulos?stock_bajo=true"
    });
  }
  if (stockBajo.warning?.length > 0) {
    stockAlerts.push({
      id: "stock-warning",
      type: "stock_warning",
      title: `📦 ${stockBajo.warning.length} artículo(s) con stock bajo`,
      message: stockBajo.warning.slice(0, 3).map(a => `${a.nombre}: ${a.stock_actual}`).join(", ") + (stockBajo.warning.length > 3 ? "..." : ""),
      priority: "media",
      created_at: new Date().toISOString(),
      read: false,
      isStock: true,
      link: "/inventario/articulos?stock_bajo=true"
    });
  }

  // Build mensajeria alerts
  const mensajeriaAlertItems = mensajeriaAlertas.slice(0, 5).map(a => ({
    id: `mensajeria-${a.id}`,
    type: a.tipo,
    title: a.titulo,
    message: a.descripcion,
    priority: a.prioridad,
    created_at: a.fecha_creacion,
    read: false,
    isMensajeria: true,
    link: "/mensajeria/solicitudes",
    solicitudId: a.id,
    diasRetraso: a.dias_retraso,
    esUrgente: a.es_urgente
  }));

  // Build pagos alerts
  const pagosAlertItems = pagosAlertas.slice(0, 5).map(a => ({
    id: `pagos-${a.id}`,
    type: a.tipo,
    title: a.titulo,
    message: a.descripcion || `${a.proveedor_nombre} - Factura #${a.numero_factura}`,
    priority: a.prioridad,
    created_at: new Date().toISOString(),
    read: false,
    isPago: true,
    link: "/cuentas-pagar",
    facturaId: a.factura_id,
    saldo: a.saldo_pendiente,
    diasRestantes: a.dias
  }));

  // Combine all notifications
  const allItems = [
    ...stockAlerts,
    ...pagosAlertItems,
    ...mensajeriaAlertItems,
    ...alertas.map(a => ({
      id: a.id,
      type: a.tipo,
      title: a.titulo,
      message: a.mensaje,
      priority: a.prioridad,
      created_at: a.fecha_generada,
      read: false,
      isAlerta: true
    })),
    ...notifications.map(n => ({
      ...n,
      isAlerta: false
    }))
  ].sort((a, b) => {
    // Sort by priority first (alta > media > baja)
    const priorityOrder = { alta: 0, media: 1, baja: 2 };
    const pA = priorityOrder[a.priority] ?? 1;
    const pB = priorityOrder[b.priority] ?? 1;
    if (pA !== pB) return pA - pB;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const handleItemClick = (item) => {
    if (item.related_entity_type === "whatsapp_conversacion" && item.related_entity_id) {
      markAsRead(item.id);
      setIsOpen(false);
      navigate(`/whatsapp/bandeja?conv=${item.related_entity_id}&t=${Date.now()}`);
    } else if (item.isStock && item.link) {
      setIsOpen(false);
      navigate(item.link);
    } else if (item.isMensajeria && item.link) {
      setIsOpen(false);
      navigate(item.link);
    } else if (item.isPago && item.link) {
      setIsOpen(false);
      navigate(item.link);
    } else if (item.isAlerta) {
      markAlertaAsRead(item.id);
    } else {
      markAsRead(item.id);
    }
  };

  return (
    <>
    <PushActivationPrompt />
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-slate-600 hover:text-slate-900 hover:bg-slate-200"
          data-testid="notification-bell-btn"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <Badge
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs animate-pulse"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b dark:border-slate-700">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-slate-600" />
            <h3 className="font-semibold text-slate-900 dark:text-white">Notificaciones</h3>
          </div>
          {unreadCount > 0 && (
            <Badge className="bg-red-500 text-white">{unreadCount} nuevas</Badge>
          )}
        </div>
        
        {loading ? (
          <div className="py-12 text-center">
            <Loader2 className="w-8 h-8 text-slate-300 animate-spin mx-auto" />
          </div>
        ) : allItems.length === 0 ? (
          <div className="py-12 text-center">
            <Bell className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500 dark:text-slate-400 text-sm">No hay notificaciones</p>
          </div>
        ) : (
          <ScrollArea className="h-80">
            <div className="divide-y dark:divide-slate-700">
              {allItems.map((item) => (
                <div
                  key={`${item.isAlerta ? 'alerta' : item.isStock ? 'stock' : item.isMensajeria ? 'mensajeria' : item.isPago ? 'pago' : 'notif'}-${item.id}`}
                  className={`p-4 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer ${
                    !item.read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
                  }`}
                  onClick={() => handleItemClick(item)}
                  data-testid={`notification-item-${item.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      item.isAlerta || item.isStock || item.isMensajeria || item.isPago ? getPriorityColor(item.priority) : "bg-slate-100"
                    }`}>
                      {item.isMensajeria ? (
                        item.type === "sin_mensajero" ? <User className="w-4 h-4 text-orange-600" /> : <Truck className="w-4 h-4 text-red-600" />
                      ) : item.isPago ? (
                        <Receipt className="w-4 h-4 text-red-600" />
                      ) : getIconByType(item.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className={`text-sm font-medium truncate ${
                          !item.read ? 'text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400'
                        }`}>
                          {item.title}
                        </h4>
                        <div className="flex items-center gap-1">
                          {item.isAlerta && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0 border-amber-300 text-amber-600">
                              Alerta
                            </Badge>
                          )}
                          {item.isStock && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0 border-blue-300 text-blue-600">
                              Inventario
                            </Badge>
                          )}
                          {item.isMensajeria && (
                            <Badge variant="outline" className={`text-[10px] px-1 py-0 ${
                              item.esUrgente ? "border-red-400 text-red-600 bg-red-50" : "border-purple-300 text-purple-600"
                            }`}>
                              {item.esUrgente ? "Urgente" : "Mensajería"}
                            </Badge>
                          )}
                          {item.isPago && (
                            <Badge variant="outline" className={`text-[10px] px-1 py-0 ${
                              item.priority === "critica" ? "border-red-400 text-red-600 bg-red-50" : 
                              item.priority === "alta" ? "border-orange-400 text-orange-600 bg-orange-50" : 
                              "border-yellow-300 text-yellow-600"
                            }`}>
                              Factura
                            </Badge>
                          )}
                          {!item.read && (
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                              item.priority === "alta" ? "bg-red-500" :
                              item.priority === "media" ? "bg-amber-500" : "bg-blue-500"
                            }`} />
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mb-2">
                        {item.message}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 text-slate-400">
                          <Clock className="w-3 h-3" />
                          <span className="text-xs">{formatDate(item.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          {!item.read && !item.isAlerta && !item.isStock && !item.isMensajeria && !item.isPago && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); markAsRead(item.id); }}
                              className="h-6 px-2 text-xs hover:bg-slate-200 dark:hover:bg-slate-700"
                            >
                              <Check className="w-3 h-3" />
                            </Button>
                          )}
                          {!item.isAlerta && !item.isStock && !item.isMensajeria && !item.isPago && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); deleteNotification(item.id); }}
                              className="h-6 px-2 text-xs hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/20"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}

        <div className="p-3 border-t dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <Button 
            variant="ghost" 
            className="w-full text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            onClick={() => {
              setIsOpen(false);
              window.location.href = "/gestion-humana/parametros";
            }}
          >
            Ver historial completo
          </Button>
        </div>
      </PopoverContent>
    </Popover>
    </>
  );
}
